import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import * as React from "react";
import { AI_AGENTS, MODEL_TIERS, getAiModelsAllocation, setAiModelsAllocation } from "./aiModelsAllocation";

// Allowed % options for the per-agent allocation dropdown — 5% steps.
const PCT_OPTIONS = Array.from({ length: 21 }, (_, i) => i * 5);

const AiModelsPopup = ({ organization, isOpen, onClose, onSaved }) => {
  const toast = useToast();
  const [annualLimitGbp, setAnnualLimitGbp] = React.useState("");
  const [contractStartDate, setContractStartDate] = React.useState("");
  const [agents, setAgents] = React.useState([]);

  React.useEffect(() => {
    if (!organization || !isOpen) return;
    const allocation = getAiModelsAllocation(organization.tenantId);
    setAnnualLimitGbp(allocation.annualLimitGbp ?? "");
    setContractStartDate(allocation.contractStartDate ?? "");
    setAgents(allocation.agents.map((a) => ({ ...a })));
  }, [organization, isOpen]);

  const totalPct = agents.reduce((sum, a) => sum + a.pct, 0);
  const isValidTotal = totalPct === 100;

  const updateAgent = (key, patch) => {
    setAgents((prev) => prev.map((a) => (a.key === key ? { ...a, ...patch } : a)));
  };

  const handleSave = () => {
    if (!organization) return;
    if (!isValidTotal) return; // Save button is disabled in this case too — belt and braces.
    setAiModelsAllocation(organization.tenantId, {
      annualLimitGbp: annualLimitGbp === "" ? null : Number(annualLimitGbp),
      contractStartDate: contractStartDate === "" ? null : contractStartDate,
      agents,
    });
    toast({
      title: "AI model allocation updated",
      description: "This is illustrative/frontend-only for now — nothing is saved to the backend yet.",
      status: "success",
      duration: 4000,
      isClosable: true,
    });
    if (onSaved) onSaved();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent bg="pureWhite">
        <ModalHeader>Manage AI Models</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Text fontSize="sm" color="gray.500" mb={4}>
            Set the annual AI budget for {organization?.name || "this organization"}, and how it's split across
            agents. Each agent's model tier (High/Medium/Low) is managed as a group of models elsewhere.
          </Text>

          <HStack spacing={5} mb={5} align="flex-start">
            <Box>
              <FormLabel fontSize="sm" mb={1}>
                Total annual limit
              </FormLabel>
              <InputGroup maxW="200px">
                <InputLeftElement pointerEvents="none" color="gray.400">
                  £
                </InputLeftElement>
                <Input
                  type="number"
                  min={0}
                  placeholder="e.g. 50000"
                  value={annualLimitGbp}
                  onChange={(e) => setAnnualLimitGbp(e.target.value)}
                  _focus={{ borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" }}
                />
              </InputGroup>
            </Box>
            <Box>
              <FormLabel fontSize="sm" mb={1}>
                Contract start date
              </FormLabel>
              <Input
                type="date"
                maxW="200px"
                value={contractStartDate}
                onChange={(e) => setContractStartDate(e.target.value)}
                _focus={{ borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" }}
              />
            </Box>
          </HStack>

          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Agent</Th>
                <Th>% of total limit</Th>
                <Th>Model tier</Th>
              </Tr>
            </Thead>
            <Tbody>
              {AI_AGENTS.map((agentDef) => {
                const agent = agents.find((a) => a.key === agentDef.key);
                if (!agent) return null;
                return (
                  <Tr key={agentDef.key}>
                    <Td>{agentDef.label}</Td>
                    <Td>
                      <Select
                        size="sm"
                        w="110px"
                        value={agent.pct}
                        onChange={(e) => updateAgent(agentDef.key, { pct: Number(e.target.value) })}
                        _focus={{ borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" }}
                      >
                        {PCT_OPTIONS.map((pct) => (
                          <option key={pct} value={pct}>
                            {pct}%
                          </option>
                        ))}
                      </Select>
                    </Td>
                    <Td>
                      <Select
                        size="sm"
                        w="130px"
                        value={agent.tier}
                        onChange={(e) => updateAgent(agentDef.key, { tier: e.target.value })}
                        _focus={{ borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" }}
                      >
                        {MODEL_TIERS.map((tier) => (
                          <option key={tier} value={tier}>
                            {tier}
                          </option>
                        ))}
                      </Select>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>

          <HStack justify="space-between" mt={3}>
            <Text fontSize="sm" color={isValidTotal ? "gray.500" : "status.red"} fontWeight={isValidTotal ? "normal" : "600"}>
              Total: {totalPct}%
            </Text>
          </HStack>

          {!isValidTotal && (
            <Alert status="error" mt={2} borderRadius="md">
              <AlertIcon />
              Allocations must add up to 100% ({totalPct}% currently). Adjust one or more agents before saving.
            </Alert>
          )}
        </ModalBody>

        <ModalFooter>
          <HStack>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} isDisabled={!isValidTotal}>
              Save
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AiModelsPopup;
